import { ApmBase } from '@elastic/apm-rum';
import { requestIdleCallback, waitFor } from './settimeout-wrapper';

/* Currently APM waits only 1s after page load before it reports on LCP for the user. Google say browsers stop LCP reporting after the first interaction
 * https://web.dev/articles/lcp#:~:text=The%20browser%20will%20stop%20reporting%20new%20entries%20as%20soon%20as%20the%20user%20interacts%20with%20the%20page
 *
 * This disparity often means LCP is reported too early by APM meaning pages report a better LCP score than Google would otherwise measure.
 *
 * Elastic have admitted this flaw in APM and have suggested something along the lines of below as a workaround, until it is otherwise patched https://github.com/elastic/apm-agent-rum-js/issues/1554
 *
 * Here we extend the capturing window until the first interaction is captured, the page is hidden, or by an additional 4s (5s delay below minus the current 1s), whichever comes first.
 */

export const extendTransactionWindowForBetterLCPReporting = (apm: ApmBase) => {
  const interactionCaptured = new Promise(resolve => {
    ['keydown', 'click'].forEach(type => {
      // Wrap in a setTimeout so the callback is run in a separate task
      // to avoid extending the keyboard/click handler to reduce INP impact
      // https://github.com/GoogleChrome/web-vitals/issues/383
      window.addEventListener(type, () => requestIdleCallback(resolve), {
        once: true,
        capture: true,
      });
    });
  });

  const pageHidden = new Promise(resolve => {
    window.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'hidden') {
          requestIdleCallback(resolve);
        }
      },
      { once: true },
    );
  });

  window.addEventListener('load', () => {
    const FALLBACK_PAGE_LOAD_DELAY = 5000;
    // retrieve the current Elastic APM transaction (page load)
    const tr = apm.getCurrentTransaction();

    // Exit if the transaction cannot be found for whatever reason.
    if (!tr) {
      return;
    }

    // Attach a blocking span to the transaction, preventing the transaction closing until the span ends
    const span = tr.startSpan('delay-span-ignore', 'delay', { blocking: true });

    // Exit if the span is not created as TypeScript calls out this could happen.
    if (!span) {
      return;
    }

    // Race a fallback promise with custom promise that resolves when an interaction is captured or the page is hidden.
    Promise.race([waitFor(FALLBACK_PAGE_LOAD_DELAY), interactionCaptured, pageHidden]).finally(
      () => {
        span.end();
      },
    );
  });
};
